import React from 'react';
import { useParams } from "react-router";
import classNames from "classnames";

import { ContentEditors, ModuleMap, PageModule, PageModuleType, Sponsor } from "../../../../types/working-model";
import { getStylingOverrides } from "../../utils";
import { ParamsProps } from "../../live-event";
import { SponsorsLayoutTypesClassic } from "../../../../types/template-layouts";
import SponsorCard from './sponsor-card';
import { TypographyItem } from "../../../general-ui/typography-item/typography-item";
import { getDefaultModuleAlignment, getTextValue } from "../../../../utils/utils";
import RenderEditorDescription from '../../../general-ui/editable-text/render-editor-description';
import { useTypedSelector } from '../../../../store/reducers/use-typed-selector';
import '../../../../scss/live-event/base/modules/sponsors.scss';
import { useTranslate } from '../../../../i18n/useTranslationModules';
import useTranslationRoute from '../../hooks/use-translation-route';

interface SponsorsProps {
	module: PageModule;
	template: string;
	handleScroll?: (moduleId?: number) => void;
	isLandingPage?: boolean;
}

const Sponsors = React.forwardRef<HTMLDivElement, SponsorsProps>(({ module, template, handleScroll, isLandingPage }, ref) => {

	const { language }: ParamsProps = useParams();
	const { content, styling_overrides, modules } = module;
	const layout_type = content?.layout_type || SponsorsLayoutTypesClassic.standard;

	const eventBundleContentEditor = useTypedSelector(state => state.LiveEventReducer.eventBundle?.settings?.content_editor);
	const workingEventContentEditor = useTypedSelector(state => state.CreateEventReducer.workingEvent?.settings?.content_editor);
	const contentEditor = eventBundleContentEditor || workingEventContentEditor;
	const translationRoute = useTranslationRoute();
	const { t } = useTranslate(translationRoute);
	//init as one variable so it can be passed to the card
	const stylingOverrides = getStylingOverrides(styling_overrides);
	const {
		backgroundColor,
		backgroundImg,
		titleOverrides,
		descriptionOverrides,
		customHeadingOverrides
	} = stylingOverrides;

	const { custom_heading, description, title } = getTextValue(content, language);

	return module.is_on && content && (
		<section
			id={ModuleMap[PageModuleType.sponsors]}
			className={classNames(
				"sponsors",
				`sponsors-${layout_type}`,
				template,
				backgroundColor
			)}
			ref={ref}
		>
			<div className="module-container" style={backgroundImg}>
				<div className="sponsors-text-wrapper">
					<TypographyItem className={classNames('evt-caption-1 sponsors-custom-heading', customHeadingOverrides)}>
						{t(`sponsors.${module.id}.heading`, t('sponsors.heading', custom_heading))}
					</TypographyItem>
					<TypographyItem className={classNames('evt-heading-2 sponsors-title', titleOverrides)} tagName="h2">
						{t(`sponsors.${module.id}.title`, t('sponsors.title', title))}
					</TypographyItem>
					<TypographyItem className={classNames('evt-body-text-1 sponsors-description')}>
						<RenderEditorDescription
							description={t(`sponsors.${module.id}.description`, t('sponsors.description', description))}
							descriptionOverrides={classNames(descriptionOverrides)}
							useNew={contentEditor === ContentEditors.quill}
							defaultTextAlignment={getDefaultModuleAlignment(module, template)}
						/>
					</TypographyItem>
				</div>
				<ul className="sponsor-cards-container">
					{modules?.map((sponsor: Sponsor) => {
						return (
							<SponsorCard
								key={sponsor.sponsor}
								stylingOverrides={stylingOverrides}
								sponsor={sponsor}
								template={template}
								isLandingPage={isLandingPage}
								handleScroll={(moduleId) => {
									handleScroll?.(moduleId);
								}}
							/>
						);
					}) ?? []}
				</ul>
			</div>
		</section >
	);
});

Sponsors.displayName = 'Sponsors';

export default Sponsors;
